import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Section, Box, Flex, TextField, Text, Button, Table, Dialog, Select } from '@radix-ui/themes';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import PageTitle from 'components/layout/PageTitle';
import { ConfirmDialog } from 'components/dialog';
import SimpleEditor from 'components/editor/SimpleEditor';

import { instance } from 'api/axios.instance';
import { selectType, selectRegion } from 'store/slices/users';
import { localList, regions } from 'app/constants';
import { parseIssueYear, ISSUE_YEAR_ERROR } from './localIssueYear';

const validationSchema = Yup.object({
  year: Yup.string()
    .required(ISSUE_YEAR_ERROR)
    .test('classification-year', ISSUE_YEAR_ERROR, value => parseIssueYear(value) !== undefined),
  title: Yup.string().required('제목을 입력해주세요.').max(200, '제목은 200자 이하로 입력해주세요.')
});

// 필드값 → 저장용 HTML content 생성
function buildContentHtml(template, fieldValues) {
  let html = `<h2>${template.formName}</h2>`;
  for (const f of template.fieldDefs) {
    const val = fieldValues[f.marker] || '';
    html += `<h3>${f.label}</h3>`;
    if (f.type === 'longtext') {
      html += val || '<p></p>';
    } else {
      html += `<p>${val || ''}</p>`;
    }
  }
  return html;
}

function toPreviewUrl(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${process.env.REACT_APP_API_BASE}${url}`;
}

// 지역 이슈 파인더 > 등록/수정
export default function LocalIssueFinderWrite() {
  const { id } = useParams();
  const navigate = useNavigate();
  const type = useSelector(selectType);
  const userRegion = useSelector(selectRegion);
  const userRegionCode = regions[userRegion] ?? userRegion ?? '';
  const canSelectRegion = type === 'master' || type === 'admin';
  const thumbnailInputRef = useRef(null);

  const [registrationYear] = useState(() => String(new Date().getFullYear()));
  const [isEditMode, setIsEditMode] = useState(false);
  const [isOpenSaveDialog, setIsOpenSaveDialog] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [isThumbPreviewOpen, setIsThumbPreviewOpen] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const fileInputRef = useRef(null);
  const savedFieldValuesRef = useRef(null);

  // 양식 관련 상태
  const [templates, setTemplates] = useState([]);
  const [linkedFormId, setLinkedFormId] = useState('');
  const [linkedTemplate, setLinkedTemplate] = useState(null);
  const [fieldValues, setFieldValues] = useState({});

  const formik = useFormik({
    initialValues: {
      year: registrationYear,
      title: '',
      content: '',
      region: userRegionCode,
      thumbnailUrl: '',
      thumbnailName: ''
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => setIsOpenSaveDialog(true)
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await instance('/form/templates', { method: 'GET' });
      setTemplates(res.data.data || []);
    } catch {
      setTemplates([]);
    }
  }, []);

  const fetchDetail = useCallback(async () => {
    try {
      const response = await instance(`/local-issue/${id}`, { method: 'GET' });
      if (response.data.status === 200) {
        const data = response.data.data;
        formik.setValues({
          year: data.year == null ? '' : String(data.year),
          title: data.title || '',
          content: data.content || '',
          region: data.region || userRegionCode,
          thumbnailUrl: data.thumbnailUrl || '',
          thumbnailName: data.thumbnailName || ''
        });
        if (data.thumbnailUrl) {
          setThumbnailPreview(toPreviewUrl(data.thumbnailUrl));
        }
        if (data.linkedFormId) {
          if (data.fieldValuesJson) {
            try {
              savedFieldValuesRef.current = JSON.parse(data.fieldValuesJson);
            } catch {}
          }
          setLinkedFormId(data.linkedFormId);
        }
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        alert('접근 권한이 없는 지역의 이슈입니다.');
        navigate('/localIssueFinder', { replace: true });
        return;
      }
      throw error;
    }
  }, [id, userRegionCode, navigate]);

  const fetchExistingFiles = useCallback(async () => {
    try {
      const res = await instance(`/local-issue/${id}/files`, { method: 'GET' });
      setExistingFiles(res.data.data || []);
    } catch {
      setExistingFiles([]);
    }
  }, [id]);

  useEffect(() => {
    fetchTemplates();
    if (id) {
      setIsEditMode(true);
      fetchDetail();
      fetchExistingFiles();
    }
  }, [id, fetchDetail, fetchTemplates, fetchExistingFiles]);

  // linkedFormId 세팅 후 templateDetail 로드 + 저장된 fieldValues 복원
  useEffect(() => {
    if (linkedFormId && !linkedTemplate && templates.length > 0) {
      instance(`/form/template/${linkedFormId}`, { method: 'GET' })
        .then(res => {
          if (res.data.status === 200) {
            const tpl = res.data.data;
            let defs = [];
            try {
              defs = JSON.parse(tpl.fieldDef || '[]');
            } catch {
              defs = [];
            }
            setLinkedTemplate({ ...tpl, fieldDefs: defs });
            if (savedFieldValuesRef.current) {
              setFieldValues(savedFieldValuesRef.current);
              savedFieldValuesRef.current = null;
            } else {
              const init = {};
              defs.forEach(f => {
                init[f.marker] = '';
              });
              setFieldValues(init);
            }
          }
        })
        .catch(() => {});
    }
  }, [linkedFormId, templates, linkedTemplate]);

  // 양식 선택 핸들러
  const handleFormSelect = async formId => {
    setLinkedFormId(formId);
    setFieldValues({});
    if (!formId) {
      setLinkedTemplate(null);
      return;
    }
    try {
      const res = await instance(`/form/template/${formId}`, { method: 'GET' });
      if (res.data.status === 200) {
        const tpl = res.data.data;
        let defs = [];
        try {
          defs = JSON.parse(tpl.fieldDef || '[]');
        } catch {
          defs = [];
        }
        setLinkedTemplate({ ...tpl, fieldDefs: defs });
        const init = {};
        defs.forEach(f => {
          init[f.marker] = '';
        });
        setFieldValues(init);
      }
    } catch {
      setLinkedTemplate(null);
    }
  };

  const handleFieldChange = (marker, value) => {
    setFieldValues(prev => ({ ...prev, [marker]: value }));
  };

  const handleThumbnailSelect = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingThumb(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await instance('/local-issue/thumbnail/upload', {
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.status === 201) {
        const url = res.data.data;
        formik.setFieldValue('thumbnailUrl', url);
        formik.setFieldValue('thumbnailName', file.name);
        setThumbnailPreview(toPreviewUrl(url));
      }
    } catch {
      alert('썸네일 업로드에 실패했습니다.');
    } finally {
      setUploadingThumb(false);
    }
  };

  const removeThumbnail = () => {
    formik.setFieldValue('thumbnailUrl', '');
    formik.setFieldValue('thumbnailName', '');
    setThumbnailPreview(null);
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
  };

  const formatFileSize = bytes => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleAttachFiles = e => {
    const newFiles = Array.from(e.target.files);
    setPendingFiles(prev => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemovePendingFile = index => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingFile = async fileId => {
    if (!window.confirm('파일을 삭제하시겠습니까?')) return;
    try {
      await instance(`/local-issue/files/${fileId}`, { method: 'DELETE' });
      setExistingFiles(prev => prev.filter(f => f.id !== fileId));
    } catch {
      alert('파일 삭제에 실패했습니다.');
    }
  };

  const handleSave = async () => {
    const year = parseIssueYear(formik.values.year);
    if (year === undefined) {
      alert(ISSUE_YEAR_ERROR);
      return;
    }
    const content = linkedTemplate && linkedTemplate.fieldDefs.length > 0 ? buildContentHtml(linkedTemplate, fieldValues) : '';

    if (linkedTemplate) {
      const missing = linkedTemplate.fieldDefs
        .filter(f => f.required && !fieldValues[f.marker]?.replace(/<[^>]*>/g, '').trim())
        .map(f => f.label);
      if (missing.length > 0) {
        alert(`필수 항목을 입력해주세요:\n${missing.join(', ')}`);
        return;
      }
    }

    const mode = isEditMode ? 'edit' : 'create';
    const payload = {
      ...formik.values,
      year,
      content,
      region: canSelectRegion ? formik.values.region : userRegionCode,
      linkedFormId: linkedFormId || null,
      fieldValuesJson: linkedTemplate && Object.keys(fieldValues).length > 0 ? JSON.stringify(fieldValues) : null
    };
    if (isEditMode) payload.id = Number(id);

    const response = await instance(`/local-issue/${mode}`, {
      method: 'POST',
      data: payload
    });

    if (response.data.status === 201) {
      const issueId = response.data.data;
      for (const file of pendingFiles) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          await instance(`/local-issue/${issueId}/files`, {
            method: 'POST',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch {
          // 파일 하나 실패해도 계속 진행
        }
      }
      navigate(`/localIssueFinder/detail/${issueId}`);
    }
  };

  // 필드 타입별 입력 컴포넌트 렌더링
  const renderFieldInput = field => {
    const value = fieldValues[field.marker] || '';
    const onChange = val => handleFieldChange(field.marker, val);

    if (field.type === 'longtext') {
      return (
        <SimpleEditor
          data={value}
          onChange={onChange}
          placeholder={field.placeholder || `${field.label}을(를) 입력하세요`}
          minHeight={500}
        />
      );
    }
    if (field.type === 'date') {
      return (
        <input
          type="date"
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{
            padding: '7px 10px',
            borderRadius: '4px',
            border: '1px solid var(--border-light)',
            fontSize: '14px',
            width: '180px'
          }}
        />
      );
    }
    if (field.type === 'select') {
      const options = field.options || [];
      return (
        <Select.Root value={value} onValueChange={val => onChange(val)}>
          <Select.Trigger placeholder={field.placeholder || `${field.label} 선택`} style={{ minWidth: '200px' }} />
          <Select.Content position="popper">
            {options.map(opt => (
              <Select.Item key={opt} value={opt}>
                {opt}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      );
    }
    if (field.type === 'number') {
      return (
        <TextField.Root
          type="number"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder || `${field.label}`}
        />
      );
    }
    return (
      <TextField.Root
        type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={field.placeholder || field.label}
      />
    );
  };

  const regionOptions = localList.filter(r => r.code !== '');

  const fileTableColumns = (
    <Table.Row>
      <Table.ColumnHeaderCell>파일명</Table.ColumnHeaderCell>
      <Table.ColumnHeaderCell width="100px" justify="center">
        크기
      </Table.ColumnHeaderCell>
      <Table.ColumnHeaderCell width="100px" justify="center">
        관리
      </Table.ColumnHeaderCell>
    </Table.Row>
  );

  return (
    <Section className="template-form-wrap">
      <PageTitle title={`지역 이슈 ${isEditMode ? '수정' : '등록'}`} />
      <form
        onSubmit={e => {
          e.preventDefault();
          formik.handleSubmit();
        }}
      >
        {/* 기본 정보 */}
        <Box className="box box-white">
          <Table.Root className="table-write">
            <Table.Body>
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text className="required-start">분류 연도</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <TextField.Root
                    size="2"
                    id="year"
                    name="year"
                    type="number"
                    required
                    min="1000"
                    max="9999"
                    step="1"
                    aria-label="분류 연도"
                    aria-invalid={Boolean(formik.touched.year && formik.errors.year)}
                    aria-describedby={formik.touched.year && formik.errors.year ? 'issue-year-error' : undefined}
                    value={formik.values.year}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{ maxWidth: '180px' }}
                  />
                  {formik.touched.year && formik.errors.year && (
                    <Text as="p" id="issue-year-error" mt="1" className="font-error body3">
                      {formik.errors.year}
                    </Text>
                  )}
                </Table.Cell>
              </Table.Row>

              {/* 지역 */}
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text className="required-start">지역</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  {canSelectRegion ? (
                    <Select.Root value={formik.values.region} onValueChange={val => formik.setFieldValue('region', val)}>
                      <Select.Trigger placeholder="지역 선택" />
                      <Select.Content position="popper">
                        {regionOptions.map(item => (
                          <Select.Item key={item.code} value={item.code}>
                            {item.name}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  ) : (
                    <TextField.Root readOnly value={localList.find(r => r.code === userRegionCode)?.name || userRegion || ''} />
                  )}
                </Table.Cell>
              </Table.Row>

              {/* 제목 */}
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text className="required-start">제목</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <TextField.Root
                    id="title"
                    name="title"
                    required
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="제목을 입력하세요"
                  />
                  {formik.touched.title && formik.errors.title && (
                    <Text as="p" mt="1" className="font-error body3">
                      {formik.errors.title}
                    </Text>
                  )}
                </Table.Cell>
              </Table.Row>

              {/* 썸네일 */}
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text>썸네일</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <Flex align="center" gap="3">
                    <input
                      ref={thumbnailInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleThumbnailSelect}
                    />
                    <Button
                      type="button"
                      size="2"
                      variant="outline"
                      onClick={() => thumbnailInputRef.current?.click()}
                      disabled={uploadingThumb}
                    >
                      {uploadingThumb ? '업로드 중...' : '이미지 선택'}
                    </Button>
                    {thumbnailPreview && (
                      <>
                        <img
                          src={thumbnailPreview}
                          alt="썸네일 미리보기"
                          title="클릭하면 크게 볼 수 있습니다"
                          onClick={() => setIsThumbPreviewOpen(true)}
                          style={{
                            height: '60px',
                            aspectRatio: '16/9',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid var(--border-light)',
                            cursor: 'zoom-in'
                          }}
                        />
                        <Button type="button" size="1" variant="outline" color="red" onClick={removeThumbnail}>
                          제거
                        </Button>
                      </>
                    )}
                    {!thumbnailPreview && (
                      <Text size="2" color="gray">
                        16:9 비율 이미지를 권장합니다.
                      </Text>
                    )}
                  </Flex>
                </Table.Cell>
              </Table.Row>

              {/* 관련 양식 */}
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text>관련 양식</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <Flex align="center" gap="3">
                    {templates.length > 0 ? (
                      <Select.Root value={linkedFormId} onValueChange={handleFormSelect}>
                        <Select.Trigger placeholder="양식을 선택하면 아래에 입력 항목이 나타납니다" style={{ minWidth: '320px' }} />
                        <Select.Content position="popper">
                          {templates.map(t => (
                            <Select.Item key={t.formId} value={t.formId}>
                              {t.formName}
                              {t.category ? ` (${t.category})` : ''}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    ) : (
                      <Text size="2" color="gray">
                        등록된 HWPX 양식이 없습니다.
                      </Text>
                    )}
                    {linkedFormId && (
                      <Button
                        type="button"
                        size="1"
                        variant="ghost"
                        color="gray"
                        onClick={() => {
                          setLinkedFormId('');
                          setLinkedTemplate(null);
                          setFieldValues({});
                        }}
                      >
                        연결 해제
                      </Button>
                    )}
                  </Flex>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table.Root>
        </Box>

        {/* 양식 선택됨 → 변수 입력 폼 */}
        {linkedTemplate && linkedTemplate.fieldDefs.length > 0 && (
          <Box className="box box-white">
            <Text as="h3" size="3" weight="bold" mb="4">
              내용 입력 — {linkedTemplate.formName}
            </Text>
            {linkedTemplate.description && (
              <Text size="2" color="gray" mb="4" style={{ display: 'block' }}>
                {linkedTemplate.description}
              </Text>
            )}
            <Table.Root className="table-write">
              <Table.Body>
                {linkedTemplate.fieldDefs.map(field => (
                  <Table.Row key={field.marker} align={field.type === 'longtext' ? 'start' : 'center'}>
                    <Table.ColumnHeaderCell colSpan={2}>
                      <Text className={field.required ? 'required-start' : ''}>{field.label}</Text>
                      <Text as="p" size="1" color="gray" style={{ fontFamily: 'monospace', marginTop: '2px' }}>
                        {field.marker}
                      </Text>
                    </Table.ColumnHeaderCell>
                    <Table.Cell colSpan={10}>{renderFieldInput(field)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}

        {/* 첨부파일 */}
        <Box className="box box-white">
          <Flex justify="between" align="center" mb="3">
            <Text size="3" weight="bold">
              첨부파일
            </Text>
            <>
              <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleAttachFiles} />
              <Button type="button" size="2" variant="outline" onClick={() => fileInputRef.current?.click()}>
                파일 첨부
              </Button>
            </>
          </Flex>

          {/* 기존 파일 (수정 모드) */}
          {isEditMode && existingFiles.length > 0 && (
            <Box mb="4">
              <Text size="2" weight="bold" mb="2" style={{ display: 'block', color: 'var(--gray-11)' }}>
                기존 파일
              </Text>
              <Table.Root variant="surface" size="2">
                <Table.Header>{fileTableColumns}</Table.Header>
                <Table.Body>
                  {existingFiles.map(f => (
                    <Table.Row key={f.id} align="center">
                      <Table.Cell>
                        <Text
                          size="2"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '420px',
                            display: 'block'
                          }}
                        >
                          {f.fileName}
                        </Text>
                      </Table.Cell>
                      <Table.Cell justify="center">
                        <Text size="2">{formatFileSize(f.fileSize)}</Text>
                      </Table.Cell>
                      <Table.Cell justify="center">
                        <Button type="button" size="1" variant="outline" color="red" onClick={() => handleDeleteExistingFile(f.id)}>
                          삭제
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}

          {/* 새로 추가할 파일 */}
          {pendingFiles.length > 0 && (
            <Box>
              {isEditMode && existingFiles.length > 0 && (
                <Text size="2" weight="bold" mb="2" style={{ display: 'block', color: 'var(--gray-11)' }}>
                  추가 파일
                </Text>
              )}
              <Table.Root variant="surface" size="2">
                <Table.Header>{fileTableColumns}</Table.Header>
                <Table.Body>
                  {pendingFiles.map((file, idx) => (
                    <Table.Row key={idx} align="center">
                      <Table.Cell>
                        <Text
                          size="2"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '420px',
                            display: 'block'
                          }}
                        >
                          {file.name}
                        </Text>
                      </Table.Cell>
                      <Table.Cell justify="center">
                        <Text size="2">{formatFileSize(file.size)}</Text>
                      </Table.Cell>
                      <Table.Cell justify="center">
                        <Button type="button" size="1" variant="outline" color="red" onClick={() => handleRemovePendingFile(idx)}>
                          삭제
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}

          {existingFiles.length === 0 && pendingFiles.length === 0 && (
            <Text size="2" color="gray">
              첨부할 파일을 선택해주세요.
            </Text>
          )}
        </Box>

        {/* 버튼 */}
        <Box>
          <Flex gap="var(--space-2)" justify="end">
            <Dialog.Root>
              <Dialog.Trigger>
                <Button size="2" variant="outline" type="button">
                  취소
                </Button>
              </Dialog.Trigger>
              <ConfirmDialog
                title="저장 취소"
                description="작성중인 내용을 저장하지 않고 나가시겠습니까?"
                onConfirm={() => navigate('/localIssueFinder')}
              />
            </Dialog.Root>
            <Button size="2" type="submit">
              {isEditMode ? '수정' : '저장'}
            </Button>
            <Dialog.Root open={isOpenSaveDialog} onOpenChange={setIsOpenSaveDialog}>
              <ConfirmDialog title="저장 확인" description="저장하시겠습니까?" onConfirm={handleSave} />
            </Dialog.Root>
          </Flex>
        </Box>
        {/* 썸네일 원본 보기 */}
        <Dialog.Root open={isThumbPreviewOpen} onOpenChange={setIsThumbPreviewOpen}>
          <Dialog.Content maxWidth="860px">
            <Dialog.Title>썸네일 미리보기</Dialog.Title>
            <Box mt="2">
              <img src={thumbnailPreview} alt="썸네일 원본" style={{ width: '100%', borderRadius: '6px', display: 'block' }} />
            </Box>
            <Flex justify="end" mt="3">
              <Dialog.Close>
                <Button variant="outline" type="button" size="2">
                  닫기
                </Button>
              </Dialog.Close>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </form>
    </Section>
  );
}
